import { createItem, deleteItem, getItem, listItems, updateItem } from './sharepoint';

const LIST = 'invoices';

/**
 * Invoice statuses. The full payment lifecycle the Director walks
 * each invoice through manually (Proxemics emails invoices outside
 * the portal — the portal record's status is updated by hand on the
 * Invoice Tracker tab).
 *
 *   Draft            — in-flight state while a Director is composing
 *                      a new invoice (line items still editable,
 *                      hasn't been issued to the customer).
 *   Sent             — PDF was generated and emailed to the customer.
 *                      Automatically set when an invoice is created
 *                      from the draft preview.
 *   Awaiting Payment — Customer has acknowledged but not yet paid.
 *   Partial Payment  — Some of the balance has been paid; remainder
 *                      still outstanding.
 *   Paid             — Payment received and reconciled.
 *   Overpaid         — Customer paid more than the invoiced amount.
 *   Duplicate Pay    — A duplicate payment was received for this invoice.
 *   Overdue          — Past due date with no payment.
 *   Cancelled        — Voided / no longer billable (e.g., disputed,
 *                      replaced by a supplemental invoice, etc.).
 *
 * Order matters — the Invoice Tracker dropdown renders these in the
 * order listed.
 */
export const INVOICE_STATUSES = [
  'Draft',
  'Sent',
  'Awaiting Payment',
  'Partial Payment',
  'Paid',
  'Overpaid',
  'Duplicate Pay',
  'Overdue',
  'Cancelled',
];

/**
 * Billing modes a Director picks at submit time. Most suppliers get
 * one combined invoice listing line items per plant inside; some
 * suppliers (or customer policies) want each plant on its own
 * separate invoice. Stored on the record so finance can audit which
 * shape was used. See createInvoice in Phase 2 for the generation
 * logic that branches on this.
 */
export const BILLING_MODES = {
  COMBINED: 'combined', //   one invoice spanning all plants for the period
  PER_PLANT: 'per-plant', // one invoice per plant for the period
};

// ---- Encode / decode -------------------------------------------------
//
// PlantCodes, LineItems, TimesheetIds, and AdjustmentIds all live as
// JSON blobs in SharePoint text columns so the schema doesn't need a
// per-line-item child list. Decode parses them out to arrays for the
// page; encode flattens them back to JSON strings on save. Same
// pattern the timesheet service uses.

function safeObject(json) {
  if (!json) return null;
  try {
    const v = JSON.parse(json);
    return v && typeof v === 'object' && !Array.isArray(v) ? v : null;
  } catch {
    return null;
  }
}

function decode(row) {
  if (!row) return row;
  const safeArray = (raw) => {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };
  return {
    ...row,
    plantCodes: safeArray(row.PlantCodesJson),
    lineItems: safeArray(row.LineItemsJson),
    timesheetIds: safeArray(row.TimesheetIdsJson).map(String),
    adjustmentIds: safeArray(row.AdjustmentIdsJson).map(String),
    // Supplier-side contact names the Director picked when composing
    // this invoice — surfaces on the PDF "Bill to" block as an
    // "Attn:" line. Up to 3 names stored as a JSON array.
    contactNames: safeArray(row.ContactNamesJson)
      .map((n) => String(n || '').trim())
      .filter(Boolean),
    // Main + Additional Billing Contacts (Oct 2026); see
    // utils/billingContacts.js. null on invoices made before then.
    billingContacts: safeObject(row.BillingContactsJson),
  };
}

function encode(model) {
  const {
    plantCodes,
    lineItems,
    timesheetIds,
    adjustmentIds,
    contactNames,
    billingContacts,
    // Drop the page-side id from the column payload — the SharePoint
    // item id is set via the URL on updateItem, never in fields.
    id: _id,
    ...rest
  } = model;
  const out = { ...rest };
  // Only encode the JSON-array columns the caller actually provided.
  // updateItem PATCHes exactly the fields it's handed, so a partial
  // update like updateInvoice(id, { Status }) must NOT include these
  // keys — unconditionally stringifying the missing arrays used to
  // send LineItemsJson: "[]" (etc.) and silently wipe the invoice's
  // line items every time the Tracker status dropdown was used.
  if ('plantCodes' in model) {
    out.PlantCodesJson = JSON.stringify(
      Array.isArray(plantCodes) ? plantCodes : [],
    );
  }
  if ('lineItems' in model) {
    out.LineItemsJson = JSON.stringify(
      Array.isArray(lineItems) ? lineItems : [],
    );
  }
  if ('timesheetIds' in model) {
    out.TimesheetIdsJson = JSON.stringify(
      Array.isArray(timesheetIds) ? timesheetIds.map(String) : [],
    );
  }
  if ('adjustmentIds' in model) {
    out.AdjustmentIdsJson = JSON.stringify(
      Array.isArray(adjustmentIds) ? adjustmentIds.map(String) : [],
    );
  }
  if ('billingContacts' in model) {
    out.BillingContactsJson = JSON.stringify(
      billingContacts && typeof billingContacts === 'object'
        ? billingContacts
        : { main: null, additional: [] },
    );
  }
  if ('contactNames' in model) {
    out.ContactNamesJson = JSON.stringify(
      Array.isArray(contactNames)
        ? contactNames.map((n) => String(n || '').trim()).filter(Boolean)
        : [],
    );
  }
  return out;
}

// ---- Reads / writes --------------------------------------------------

export async function listInvoices(opts) {
  const rows = await listItems(LIST, {
    orderby: 'fields/InvoiceNumber desc',
    ...opts,
  });
  return rows.map(decode);
}

export async function getInvoice(id) {
  return decode(await getItem(LIST, id));
}

export async function createInvoice(model) {
  return decode(await createItem(LIST, encode(model)));
}

export async function updateInvoice(id, model) {
  return decode(await updateItem(LIST, id, encode(model)));
}

export const deleteInvoice = (id) => deleteItem(LIST, id);

// ---- Helpers ---------------------------------------------------------

/**
 * Build the next invoice number (per Jillian, Sep 2026):
 *
 *   Monthly (default)   ACCT-MMDDYY            e.g. 108-093026
 *   Weekly (≤ 7 days)   ACCT-WEMMDDYY          e.g. 108-WE092626
 *   Per-plant split     ACCT-MMDDYY-PLANT      e.g. 108-093026-SHAP
 *                       (weekly + per-plant → 108-WE092626-SHAP)
 *
 * MMDDYY is the billing period's "To" date. "Weekly" means the From→To
 * period spans 7 days or fewer. If that exact number already exists
 * (say the invoice was regenerated after a correction) a -2, -3, ...
 * suffix is added so the number stays unique without blocking anyone.
 *
 * Supplemental invoices (…-A, …-B) and credit memos (CR-…) keep their
 * own schemes and are ignored here. When no account is supplied the
 * prefix falls back to 'PX' so a blank/legacy draft still gets a number.
 *
 * @param {string} accountNo   supplier account number (SupplierCode)
 * @param {object} [opts]
 * @param {string} [opts.periodStart]  YYYY-MM-DD
 * @param {string} [opts.periodEnd]    YYYY-MM-DD — drives MMDDYY
 * @param {string} [opts.plantCode]    set for per-plant split invoices
 */
export async function nextInvoiceNumber(accountNo, opts = {}) {
  const account = String(accountNo || '').trim() || 'PX';
  const { periodStart = '', periodEnd = '', plantCode = '' } = opts || {};

  const endIso = String(periodEnd || '').slice(0, 10);
  const mmddyy = /^\d{4}-\d{2}-\d{2}$/.test(endIso)
    ? `${endIso.slice(5, 7)}${endIso.slice(8, 10)}${endIso.slice(2, 4)}`
    : (() => {
        // No usable To date (shouldn't happen from the draft) — fall
        // back to today so the number is still well-formed.
        const d = new Date();
        return `${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}${String(d.getFullYear()).slice(2)}`;
      })();

  const weekly = isWeeklyPeriod(periodStart, periodEnd);
  const plant = String(plantCode || '').trim().toUpperCase();
  const base = `${account}-${weekly ? 'WE' : ''}${mmddyy}${plant ? `-${plant}` : ''}`;

  // Find every number that starts with the base, then pick the next
  // free duplicate suffix. Exact base counts as "1"; "-2", "-3" ... as
  // their number. Anything else (a "-A" supplement, a "-SHAP" plant
  // suffix on the combined base) is ignored.
  const rows = await listInvoices({
    filter: `startswith(fields/InvoiceNumber,'${base}')`,
    top: 500,
  });
  let highest = 0;
  for (const row of rows) {
    const num = String(row.InvoiceNumber || '');
    if (num === base) {
      highest = Math.max(highest, 1);
      continue;
    }
    const m = num.slice(base.length).match(/^-(\d+)$/);
    if (m) highest = Math.max(highest, parseInt(m[1], 10));
  }
  return highest === 0 ? base : `${base}-${highest + 1}`;
}

/**
 * A billing period of 7 calendar days or fewer (inclusive) is "weekly"
 * and gets the WE marker in its invoice number. Missing/invalid dates →
 * not weekly.
 */
export function isWeeklyPeriod(periodStart, periodEnd) {
  const a = parseIsoDate(periodStart);
  const b = parseIsoDate(periodEnd);
  if (!a || !b) return false;
  const days = Math.round((b - a) / 86400000) + 1;
  return days > 0 && days <= 7;
}

function parseIsoDate(value) {
  const s = String(value || '').slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/**
 * Generate the next supplemental-invoice suffix for a parent invoice
 * number. PX-2026-042-A on first call, PX-2026-042-B on second, etc.
 * Reads the existing invoices list (cheap — small list) to find the
 * highest existing suffix on that parent and increments. Returns the
 * full suffixed number ready to drop onto a new invoice record.
 */
export async function nextSupplementalInvoiceNumber(parentInvoiceNumber) {
  if (!parentInvoiceNumber) {
    throw new Error('nextSupplementalInvoiceNumber: parent required');
  }
  const rows = await listInvoices({
    filter: `startswith(fields/InvoiceNumber,'${parentInvoiceNumber}-')`,
    top: 100,
  });
  let highest = 0; // 0 → next is 'A', 1 → 'B', etc.
  for (const row of rows) {
    const tail = (row.InvoiceNumber || '').slice(parentInvoiceNumber.length + 1);
    if (/^[A-Z]$/.test(tail)) {
      const code = tail.charCodeAt(0) - 'A'.charCodeAt(0) + 1;
      if (code > highest) highest = code;
    }
  }
  const nextLetter = String.fromCharCode('A'.charCodeAt(0) + highest);
  return `${parentInvoiceNumber}-${nextLetter}`;
}

/**
 * Generate the next credit-memo number in the CR-YYYY-### sequence.
 * Credit memos use a separate number space from regular invoices so
 * the accounting trail reads cleanly ("Invoice PX-2026-042 paid in
 * full, then credited CR-2026-003"). Same best-effort caveat as
 * nextInvoiceNumber — two simultaneous generations might collide;
 * the second caller should retry.
 */
export async function nextCreditMemoNumber() {
  const year = new Date().getFullYear();
  const prefix = `CR-${year}-`;
  const rows = await listInvoices({
    filter: `startswith(fields/InvoiceNumber,'${prefix}')`,
    top: 500,
  });
  const maxSeq = rows.reduce((max, row) => {
    const num = row.InvoiceNumber || '';
    const tail = num.slice(prefix.length);
    if (!/^\d+$/.test(tail)) return max;
    const seq = parseInt(tail, 10);
    return Number.isFinite(seq) && seq > max ? seq : max;
  }, 0);
  const next = (maxSeq + 1).toString().padStart(3, '0');
  return `${prefix}${next}`;
}

/**
 * Does this invoice still "own" the trackers and adjustments on it?
 * Drafts are in flight and can be thrown away, and a Cancelled invoice
 * was thrown away (per Jillian, Oct 2026), so neither blocks the same
 * weeks from being invoiced again. Every other status does.
 */
export function invoiceHoldsBilling(inv) {
  const status = inv?.Status || '';
  return status !== 'Draft' && status !== 'Cancelled';
}

/**
 * Return the Set of timesheet ids that have already been pulled onto
 * a live invoice. Drafts don't count — they represent in-flight work
 * that hasn't actually been billed yet, so the underlying timesheets
 * are still "available" for a different draft if the Director wants
 * to throw the first one away. Cancelled invoices don't count either.
 *
 * Pass an already-loaded invoices array (page state) so callers
 * don't pay a second list-fetch every time they need the lookup.
 */
export function billedTimesheetIds(invoices) {
  const set = new Set();
  for (const inv of invoices || []) {
    if (!invoiceHoldsBilling(inv)) continue;
    for (const tid of inv.timesheetIds || []) {
      if (tid) set.add(String(tid));
    }
  }
  return set;
}

/**
 * Same idea for adjustments — supplements and credits that have
 * already landed on a non-Draft invoice (either as their own
 * invoice/credit memo or as a line on a regular invoice) shouldn't
 * be offered for re-billing.
 */
export function billedAdjustmentIds(invoices) {
  const set = new Set();
  for (const inv of invoices || []) {
    if (!invoiceHoldsBilling(inv)) continue;
    for (const aid of inv.adjustmentIds || []) {
      if (aid) set.add(String(aid));
    }
  }
  return set;
}
